import React, { useMemo } from "react";
import styled from "styled-components";
import { tokens } from "../../../public/tokens";

const stepsMap = {
    1: { label: "Review Module Selection", description: "Review and confirm your modules for the upcoming semester." },
    2: { label: "Review Account Details", description: "Review your account details and submit your re-registration." },
    5: { label: "Upload Outstanding Documents", description: "Upload any required supporting documents." }
};

const MobileStepperContainer = styled.div`
    display: none;
    @media (max-width: 1024px) {
        display: flex;
        margin-top: 1rem;
    }
`;

const StepsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const StepRow = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 1rem;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};

    ${props => props.$clickable && props.$stepVariant && `
        &:hover > div:first-child {
            background-color: ${props.$stepVariant === "done" ? tokens.colors.brand["fresh-green"][500] : tokens.colors.theme["button-hover-color"], tokens.colors.brand["aie-blue"][500]};
        }
    `}

    &::after {
        content: "";
        position: absolute;
        left: 18px;
        top: 36px;
        width: 1px;
        height: calc(100% + 1rem);
        background: var(--darkBlue, #06172a);
    }

    &:last-child::after {
        display: none;
    }
`;

const StepIcon = styled.div`
    z-index: 1;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    font-weight: 600;
    flex: 0 0 auto;
    color: var(--white);
    background-color: ${props => {
        if (props.$variant === "done") return tokens.colors.brand["fresh-green"][500];
        if (props.$variant === "active") return  `${(tokens.colors.theme["button-active-color"], tokens.colors.brand["aie-blue"][500])}`;
        return "var(--scrollColor)";
    }};
    transition: background-color 0.2s ease;
`;

const StepContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const StepTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    line-height: 14px;
    color: var(--black1);
`;

const StepDescription = styled.div`
    font-size: 12px;
    color: var(--lightGray3);
`;

const MobileStepper = ({
    currentStep,
    stepsAvailable,
    submitted,
    activeTab,
    onStepClick,
    onTabChange,
    readOnlyData,
    registration
}) => {
    const statusValue = (readOnlyData?.status ?? registration?.status ?? "").trim().toLowerCase();
    const accountPayerUpdatedValue = Number(registration?.account_payer_updated || 0);

    const isStep1Complete = statusValue === "submitted" || Boolean(submitted);
    const isStep2Complete = accountPayerUpdatedValue === 1;

    const steps = useMemo(() => {
        const effectiveCurrentStep = currentStep === 5 ? 2 : currentStep;
        
        const validSteps = stepsAvailable
            .filter(stepId => stepId !== 4) // Remove step 4 if present
            .filter(stepId => stepsMap[stepId] && stepId !== 5); // Exclude step 5 from stepper
        
        const hasStep1 = validSteps.includes(1);
        const hasStep2 = validSteps.includes(2);
        
        let finalSteps = validSteps;
        if (!hasStep1 || !hasStep2) {
            finalSteps = [1, 2].filter(step => !finalSteps.includes(step)).concat(finalSteps).sort((a, b) => a - b);
        }
        
        const stepperSteps = finalSteps
            .map((stepId, idx) => {
                return {
                    ...stepsMap[stepId],
                    id: stepId,
                    displayIndex: idx + 1,
                    isActive: effectiveCurrentStep === stepId
                };
            });
        return stepperSteps;
    }, [stepsAvailable, currentStep]);

    const hasCompletedStep = useMemo(() => {
        return isStep1Complete || isStep2Complete;
    }, [isStep1Complete, isStep2Complete]);

    const isOverviewTab = activeTab === "overview";

    const renderStep = ({ id, description, displayIndex, label, isActive }) => {
        let isDone = false;
        if (id === 1) {
            isDone = isStep1Complete;
        } else if (id === 2) {
            isDone = isStep2Complete;
        }
        
        const isClickable = onStepClick && (isDone || stepsAvailable.includes(id));
        const stepVariant = isDone ? "done" : (isActive ? "active" : "pending");
        
        const handleStepClick = () => {
            if (isClickable && onStepClick) {
                onStepClick(id);
                if (onTabChange && (id === 1 || id === 2)) {
                    onTabChange("application");
                }
            }
        };
        
        return (
            <StepRow 
                key={id} 
                $clickable={isClickable}
                $stepVariant={stepVariant}
                onClick={handleStepClick}
            >
                {isDone ? (
                    <StepIcon $variant="done">
                        <i className="fal fa-check"></i>
                    </StepIcon>
                ) : isActive ? (
                    <StepIcon $variant="active">
                        {displayIndex}
                    </StepIcon>
                ) : (
                    <StepIcon $variant="pending">
                        {displayIndex}
                    </StepIcon>
                )}
                <StepContent>
                    <StepTitle>{label}</StepTitle>
                    <StepDescription>{description}</StepDescription>
                </StepContent>
            </StepRow>
        );
    };

    // Only show on mobile when not on overview tab, or when steps are completed/submitted
    if ((!isOverviewTab || hasCompletedStep || submitted) && steps.length > 0) {
        return (
            <MobileStepperContainer>
                <StepsContainer>
                    {steps.map((step) => renderStep(step))}
                </StepsContainer>
            </MobileStepperContainer>
        );
    }

    return null;
};

export default MobileStepper;

